import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _kebabCase from 'lodash/kebabCase';
import _isEmpty from 'lodash/isEmpty';

import keycode from '../../constants/keycode';

import { BottomIcon, CloseIcon } from '../Icons/Icons';
import Option from './Option/Option';

import './select.scss';

class Select extends React.Component {
    constructor(props) {
        super(props);

        this.selectRef = React.createRef();
        this.state = {
            focused: false,
            opened: false,
            searchText: '',
        };

        this.collapse = this.collapse.bind(this);
        this.expand = this.expand.bind(this);
        this.focusInput = this.focusInput.bind(this);
        this.focusOption = this.focusOption.bind(this);
        this.focusSearch = this.focusSearch.bind(this);
        this.focusInputOrSearch = this.focusInputOrSearch.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleSearchKeyDown = this.handleSearchKeyDown.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.toggle = this.toggle.bind(this);
        this.elementBlur = this.elementBlur.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.elementBlur);
    }

    getTitle() {
        const {
            field,
            options,
            placeholder,
            assessmentLanguage,
            clearable,

        } = this.props;
        const activeOption = options.find(option =>
            option.value === (typeof field.value === 'object' ? field.value && field.value[0] : field.value));
        if (activeOption) {
            return (
                <span>
                    {activeOption.name || activeOption.value}
                    {clearable && (
                        <button
                            className="select__clear"
                            onClick={this.clearFieldValue}
                        >
                            <img
                                height={8}
                                width={8}
                                src={CloseIcon}
                                alt="CloseIcon"

                            />
                        </button>
                    )}
                </span>);
        }

        let placeholderLanguage = placeholder;
        if (assessmentLanguage) {
            if (assessmentLanguage === 'SPA' || assessmentLanguage === 'LAS') {
                placeholderLanguage = 'Seleccione';
            } else if (assessmentLanguage === 'FRE') {
                placeholderLanguage = 'Sélectionnez';
            } else if (assessmentLanguage === 'POR' || assessmentLanguage === 'POE') {
                placeholderLanguage = 'Selecione';
            } else if (assessmentLanguage === 'CHI') {
                placeholderLanguage = '选择';
            } else if (assessmentLanguage === 'CZE') {
                placeholderLanguage = 'Vyberte';
            } else if (assessmentLanguage === 'RUS') {
                placeholderLanguage = 'Выберите';
            } else if (assessmentLanguage === 'GER') {
                placeholderLanguage = 'Auswählen';
            } else if (assessmentLanguage === 'DUT') {
                placeholderLanguage = 'Selecteer';
            } else if (assessmentLanguage === 'IND') {
                placeholderLanguage = 'Pilih';
            }
        }

        return placeholderLanguage;
    }

    clearFieldValue = () => {
        const {
            field, form,
        } = this.props;
        const fieldName = field.name;
        form.setFieldValue(fieldName, null);
        this.props.onClearFieldValue();
    }

    elementBlur(event) {
        const node = this.selectRef.current;
        if (node === null) {
            return;
        }
        const isElementChildOfSelect = node.contains(event.target);
        if (isElementChildOfSelect) {
            return;
        }

        this.collapse();
    }

    focusOption() {
        const node = this.selectRef.current;
        const selectedOptionNode = node.querySelector('.select__option--selected');
        const firstOptionNode = node.querySelector('.select__option');
        setTimeout(() => {
            if (selectedOptionNode) {
                selectedOptionNode.focus();
            } else if (firstOptionNode) {
                firstOptionNode.focus();
            }
        }, 0);
    }

    focusInput() {
        const node = this.selectRef.current;
        const inputNode = node.querySelector('input');
        setTimeout(() => {
            inputNode.focus();
        }, 0);
    }

    focusSearch() {
        const node = this.selectRef.current;
        const inputNode = node.querySelector('.select__search');
        setTimeout(() => {
            inputNode.focus();
        }, 0);
    }

    focusInputOrSearch() {
        const { searchable } = this.props;
        if (searchable) {
            this.focusSearch();
        } else {
            this.focusOption();
        }
    }

    handleFocus() {
        this.setState({
            focused: true,
        });
    }

    handleBlur(event) {
        const { field } = this.props;

        this.setState({
            focused: false,
        });

        if (field && field.onBlur && typeof field.onBlur === 'function') {
            field.onBlur(event);
        }
    }

    toggle() {
        const { disabled, searchable } = this.props;
        const { opened } = this.state;

        if (disabled) {
            return;
        }

        if (searchable && opened) {
            return;
        }

        if (opened) {
            this.collapse();
        } else {
            this.expand();
        }
    }

    handleKeyPress(event) {
        const eventKeyCode = event.which || event.key || 0;

        if (eventKeyCode === keycode.ENTER || eventKeyCode === keycode.SPACE) {
            event.preventDefault();
            this.expand();
        }
    }

    handleKeyDown(event) {
        const { opened } = this.state;
        const eventKeyCode = event.which || event.key || 0;

        if (eventKeyCode === keycode.TAB && opened) {
            event.preventDefault();
            this.collapse();
        }
    }

    handleSearchKeyDown(event) {
        const eventKeyCode = event.which || event.key || 0;
        /* eslint-disable default-case */
        switch (eventKeyCode) {
            case keycode.DOWN:
            case keycode.UP:
                event.preventDefault();
                this.focusOption();
                break;
            case keycode.ESC:
            case keycode.TAB:
                event.preventDefault();
                this.collapse();
                break;
        }
        /* eslint-enable default-case */
    }

    expand() {
        document.addEventListener('click', this.elementBlur);
        this.setState({
            opened: true,
            searchText: '',
        }, () => {
            this.focusInputOrSearch();
        });
    }

    collapse() {
        document.removeEventListener('click', this.elementBlur);
        this.setState({
            opened: false,
        }, () => {
            this.focusInput();
        });
        if (this.state.searchText && this.props.shouldCreateWithSearch) {
            this.selectValues(this.state.searchText);
        }
    }

    selectValues = async (value) => {
        const {
            field, form, disabled, onSelect,
        } = this.props;
        const fieldName = field.name;
        if (disabled) {
            return;
        }
        await form.setFieldValue(fieldName, value);
        if (onSelect && typeof onSelect === 'function') {
            onSelect(value);
        }
    }
    handleSelect(value) {
        this.selectValues(value);

        this.collapse();
    }

    handleSearchChange(event) {
        const { getSearchText } = this.props;
        if (getSearchText) getSearchText(event.target.value);
        this.setState({
            searchText: event.target.value,
        });
    }

    render() {
        const {
            disabled,
            field,
            form,
            hint,
            id,
            label,
            options,
            placeholder,
            deselectable,
            className,
            ariaLabel,
            searchable,
            forceShowError,
            showWarning,
            warning,
        } = this.props;
        const { focused, opened, searchText } = this.state;
        const fieldName = field.name;
        const selectId = id || fieldName;
        const error = form.errors[fieldName];
        const isInvalid = error !== undefined && error !== null;
        const isTouched = !!form.touched[fieldName];
        const showError = isTouched && isInvalid;
        const title = this.getTitle();
        const value = field.value || '';
        const containerClassName = classNames('select__action', {
            'select__action--readonly': !searchable || !opened,
        });
        const selectClassName = classNames('select', {
            'select--opened': opened,
            'select--focused': focused,
            'select--disabled': disabled,
            'select--invalid': showError,
        }, className);
        const inputProps = {};
        if (disabled) {
            inputProps.disabled = disabled;
        }
        if (ariaLabel) {
            inputProps['aria-label'] = ariaLabel;
        }

        let filteredOptions = options
            .filter(o => o.name.toLowerCase().includes(searchText.toLowerCase()));
        filteredOptions = filteredOptions.length
            ? filteredOptions
            : [...filteredOptions, { name: searchText, value: searchText }];

        filteredOptions = searchable === false || _isEmpty(searchText)
            ? options
            : filteredOptions;
        return (
            <div
                className={selectClassName}
                ref={this.selectRef}
            >
                <div className="select__desc">
                    {
                        label ?
                            <label
                                className="select__label"
                                htmlFor={selectId}
                            >
                                {label}
                            </label>
                            :
                            null
                    }
                    <div id={`${selectId}-errors`} className="select__errors">
                        {
                            (showError || forceShowError) ?
                                <span>{error}</span>
                                :
                                null
                        }
                    </div>
                    <div id={`${selectId}-hint`} className="select__hint">
                        {
                            !showError ?
                                <span>{hint}</span>
                                :
                                null
                        }
                    </div>
                </div>
                <div className="select__trigger-wrapper">
                    <span
                        aria-hidden="true"
                        className={containerClassName}
                        onClick={this.toggle}
                    >
                        {
                            searchable && opened
                                ? <input
                                    type="text"
                                    onChange={this.handleSearchChange}
                                    className="select__search"
                                    autoComplete="off"
                                    onKeyDown={this.handleSearchKeyDown}
                                />
                                : title
                        }
                    </span>
                    <input
                        {...field}
                        {...inputProps}
                        aria-autocomplete="none"
                        autoComplete="off"
                        aria-controls={`${selectId}-choices`}
                        aria-describedby={`${selectId}-errors ${selectId}-hint`}
                        aria-expanded={opened}
                        className="select__value"
                        id={selectId}
                        onBlur={this.handleBlur}
                        onFocus={this.handleFocus}
                        onKeyPress={this.handleKeyPress}
                        onKeyDown={this.handleKeyDown}
                        readOnly
                        role="combobox"
                        value={value}
                        type="text"
                    />
                    <img
                        className="select__icon"
                        height={6}
                        width={10}
                        src={BottomIcon}
                        alt="BottomIcon"

                    />
                </div>
                <div className="select__errors select__warning">
                    {
                        showWarning ?
                            <span>{warning}</span>
                            :
                            null
                    }
                </div>
                <ul
                    aria-hidden={!opened}
                    id={`${selectId}-choices`}
                    role="listbox"
                    className="select__choices"
                >
                    {
                        deselectable && field.value ?
                            <li
                                role="presentation"
                                key={placeholder}
                            >
                                <Option
                                    isSelected={false}
                                    name={placeholder}
                                    onSelect={this.handleSelect}
                                    onCollapse={this.collapse}
                                />
                            </li>
                            :
                            null
                    }
                    {
                        filteredOptions.map((option, idx) => {
                            const key = option.value || _kebabCase(option.name) || idx;

                            return (
                                <li
                                    role="presentation"
                                    key={key}
                                >
                                    <Option
                                        {...option}
                                        isSelected={option.value === field.value}
                                        onSelect={this.handleSelect}
                                        onCollapse={this.collapse}
                                    />
                                </li>
                            );
                        })
                    }
                </ul>
            </div>
        );
    }
}

Select.propTypes = {
    disabled: PropTypes.bool,
    field: PropTypes.shape({
        name: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        value: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.number,
            PropTypes.shape({}),
            PropTypes.string,
        ]),
    }).isRequired,
    form: PropTypes.shape({
        errors: PropTypes.objectOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.array,
        ])),
        touched: PropTypes.objectOf(PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.shape({}),
            PropTypes.array,
        ])),
    }).isRequired,
    hint: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.string,
        ]),
        value: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.number,
            PropTypes.shape({}),
            PropTypes.string,
        ]).isRequired,
    })).isRequired,
    placeholder: PropTypes.string,
    onSelect: PropTypes.func,
    onClearFieldValue: PropTypes.func,
    deselectable: PropTypes.bool,
    className: PropTypes.string,
    ariaLabel: PropTypes.string,
    searchable: PropTypes.bool,
    assessmentLanguage: PropTypes.string,
    forceShowError: PropTypes.bool,
    getSearchText: PropTypes.func,
    clearable: PropTypes.bool,
    shouldCreateWithSearch: PropTypes.bool,
    showWarning: PropTypes.bool,
    warning: PropTypes.string,
};

Select.defaultProps = {
    disabled: false,
    hint: '',
    placeholder: 'Select',
    assessmentLanguage: 'ENG',
    id: '',
    label: '',
    onSelect: () => { },
    onClearFieldValue: () => { },
    deselectable: false,
    className: '',
    ariaLabel: '',
    searchable: false,
    forceShowError: false,
    getSearchText: () => { },
    clearable: true,
    shouldCreateWithSearch: false,
    showWarning: false,
    warning: '',
};

export default Select;
